import { useQuery } from '@tanstack/react-query';
import { Badge, Group, Loader, Stack, Text } from '@mantine/core';
import { IconMail } from '@tabler/icons-react';
import { api } from '../lib/api';
import type { WeclappTicketCommentRecipient } from '../lib/api';

const RECIPIENT_SOURCE_LABEL: Record<WeclappTicketCommentRecipient['source'], string> = {
  CONTACT: 'Ansprechpartner',
  SENDER: 'Absender',
  MAIL: 'An/CC im Verlauf',
};

/**
 * Wer liest einen externen Kommentar mit. Bewusst als „hängen am Ticket" formuliert:
 * zugestellt wird von Weclapp, die endgültige Empfängerwahl trifft dessen Konfiguration —
 * eine Zustellgarantie wäre gelogen und würde irgendwann jemanden kalt erwischen.
 */
export function ExternalRecipientsList({ ticketId }: { ticketId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['weclapp-comment-recipients', ticketId],
    queryFn: () => api.getWeclappTicketCommentRecipients(ticketId),
    staleTime: 120_000,
  });

  if (isLoading) {
    return (
      <Group gap={6}>
        <Loader size="xs" />
        <Text size="xs" c="dimmed">
          Empfänger werden geladen…
        </Text>
      </Group>
    );
  }

  if (error) {
    return (
      <Text size="xs" c="dimmed">
        Empfänger konnten nicht geladen werden — in Weclapp prüfen, wer am Ticket hängt.
      </Text>
    );
  }

  const recipients = data?.recipients ?? [];

  return (
    <Stack gap={4}>
      {recipients.length === 0 ? (
        <Text size="xs" fw={600}>
          Am Ticket hängt keine Kundenadresse — vermutlich geht gar keine Mail raus.
        </Text>
      ) : (
        <>
          <Text size="xs" fw={600}>
            Am Ticket hängen ({recipients.length}):
          </Text>
          {recipients.map((r) => (
            <Group key={r.email} gap={6} wrap="nowrap" align="center">
              <IconMail size={12} style={{ flexShrink: 0, opacity: 0.7 }} />
              <Text size="xs" style={{ wordBreak: 'break-all' }}>
                {r.name ? `${r.name} · ` : ''}
                {r.email}
              </Text>
              <Badge size="xs" variant="light" color={r.automated ? 'gray' : 'red'} style={{ flexShrink: 0 }}>
                {r.automated ? 'Automatik-Adresse' : RECIPIENT_SOURCE_LABEL[r.source]}
              </Badge>
            </Group>
          ))}
        </>
      )}
      {data?.contactWithoutEmail && (
        <Text size="xs" c="dimmed">
          Ansprechpartner {data.contactWithoutEmail} hat keine Mailadresse in Weclapp.
        </Text>
      )}
      {!!data?.ownAddresses.length && (
        <Text size="xs" c="dimmed">
          {data.ownAddresses.length} eigene Adresse{data.ownAddresses.length === 1 ? '' : 'n'} (@fixbyte.it)
          nicht aufgeführt.
        </Text>
      )}
      <Text size="xs" c="dimmed">
        Zugestellt wird von Weclapp — die tatsächliche Auswahl kann dort abweichen.
      </Text>
    </Stack>
  );
}
